import React, { useEffect, useState } from "react";
import { reportsApi, fmtUSD } from "../../lib/api";
import { MultiFilter } from "./CustomReportBuilder";
import EconWidget from "../econ/EconWidget";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Play, Save, Download, ArrowUpRight, ArrowDownRight, Table2, BarChart3, LineChart, AreaChart, PieChart, Gauge, Layers } from "lucide-react";
import { toast } from "sonner";

const err = (e) => e?.response?.data?.detail || e.message;
const VIZ_ICONS = { table: Table2, kpi: Gauge, bar: BarChart3, stacked_bar: Layers, line: LineChart, area: AreaChart, donut: PieChart };
const DIM_GROUPS = ["Time", "Infrastructure", "Organization", "Business", "Engineering"];

function Chip({ active, onClick, children, testId, badge }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs border transition-colors duration-150 inline-flex items-center gap-1 ${
        active ? "bg-[#00579e] text-white border-[#00579e]" : "border-border text-muted-foreground hover:border-[#00579e] hover:text-[#00579e]"
      }`}
    >
      {children}
      {badge != null && <span className="cs-mono text-[9px] bg-white/25 rounded-full px-1">{badge}</span>}
    </button>
  );
}

function V2Table({ table }) {
  const cell = (row, c) => {
    const v = row[c.key];
    if (v === null || v === undefined) return "—";
    if (c.format === "usd") return <span className="cs-mono font-semibold text-[#00579e]">{fmtUSD(v)}</span>;
    if (c.format === "pct_delta") {
      const up = v >= 0;
      return (
        <span className={`cs-mono inline-flex items-center gap-0.5 text-xs ${up ? "text-[#be185d]" : "text-[#15803d]"}`}>
          {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{Math.abs(v)}%
        </span>
      );
    }
    return typeof v === "number" ? <span className="cs-mono">{v.toLocaleString()}</span> : v;
  };
  return (
    <div className="overflow-x-auto border border-border rounded-sm max-h-[480px] overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="bg-[hsl(var(--cs-tint))] border-b border-border sticky top-0">
          <tr className="text-left">
            {table.columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-medium text-muted-foreground whitespace-nowrap ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i} className="cs-row-hover border-b border-border last:border-0">
              {table.columns.map((c) => (
                <td key={c.key} className={`px-3 py-1.5 whitespace-nowrap ${c.align === "right" ? "text-right" : "text-xs text-muted-foreground"}`}>{cell(row, c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ReportBuilderV2({ savedReport, onSaved }) {
  const [provider, setProvider] = useState(savedReport?.definition?.provider || "all");
  const [fields, setFields] = useState(null);
  const [groupBy, setGroupBy] = useState(savedReport?.definition?.group_by || ["month", "service"]);
  const [measures, setMeasures] = useState(savedReport?.definition?.measures || ["billed_cost"]);
  const [derived, setDerived] = useState(savedReport?.definition?.derived || []);
  const [filters, setFilters] = useState(savedReport?.definition?.filters || {});
  const [months, setMonths] = useState(String(savedReport?.definition?.last_n_months || 6));
  const [viz, setViz] = useState(savedReport?.definition?.viz || "stacked_bar");
  const [name, setName] = useState(savedReport?.name || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    reportsApi.v2Fields(provider).then((f) => {
      setFields(f);
      const dimIds = new Set(f.dimensions.map((d) => d.id));
      const mIds = new Set(f.measures.map((m) => m.id));
      setGroupBy((g) => g.filter((x) => dimIds.has(x)));
      setMeasures((m) => m.filter((x) => mIds.has(x)));
      setFilters((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => f.filterable.some((x) => x.id === k))));
    }).catch((e) => toast.error("Failed to load fields", { description: err(e) }));
    // eslint-disable-next-line
  }, [provider]);

  useEffect(() => {
    if (savedReport) run();
    // eslint-disable-next-line
  }, [savedReport?.id]);

  const definition = () => ({
    name: name || "Untitled report",
    provider,
    group_by: groupBy,
    measures,
    derived,
    filters,
    last_n_months: Number(months),
    viz,
  });

  const run = () => {
    if (!groupBy.length) return toast.error("Pick at least one dimension");
    if (!measures.length) return toast.error("Pick at least one measure");
    setLoading(true);
    reportsApi.v2Preview(definition())
      .then(setData)
      .catch((e) => toast.error("Preview failed", { description: err(e) }))
      .finally(() => setLoading(false));
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give the report a name first");
    try {
      await reportsApi.v2Save({ id: savedReport?.id, name: name.trim(), definition: definition() });
      toast.success("Report saved");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Save failed", { description: err(e) });
    }
  };

  const exportCsv = async () => {
    try {
      const blob = await reportsApi.v2Export(definition());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "report").replace(/\s+/g, "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed", { description: err(e) });
    }
  };

  const toggle = (list, setList, id, max) => {
    setList((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= max ? prev : [...prev, id]);
  };

  if (!fields) return <div className="cs-card p-8 text-muted-foreground text-sm" data-testid="v2-loading">Loading report builder…</div>;

  return (
    <div className="space-y-4" data-testid="report-builder-v2">
      {/* Provider + name */}
      <div className="cs-card p-4 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-24 shrink-0">Cloud</span>
          {fields.providers.map((p) => (
            <Chip key={p.id} active={provider === p.id} onClick={() => setProvider(p.id)} testId={`v2-provider-${p.id}`}>{p.label}</Chip>
          ))}
          <span className="text-[10px] text-muted-foreground cs-mono ml-auto">{fields.record_count.toLocaleString()} normalized FOCUS records</span>
        </div>

        <div className="flex items-start gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-24 shrink-0 pt-1.5">Dimensions</span>
          <div className="flex-1 space-y-1.5">
            {DIM_GROUPS.map((grp) => {
              const dims = fields.dimensions.filter((d) => d.group === grp);
              if (!dims.length) return null;
              return (
                <div key={grp} className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground/70 w-24 shrink-0">{grp}</span>
                  {dims.map((d) => (
                    <Chip key={d.id} active={groupBy.includes(d.id)} onClick={() => toggle(groupBy, setGroupBy, d.id, 4)}
                      testId={`v2-dim-${d.id}`} badge={groupBy.includes(d.id) ? groupBy.indexOf(d.id) + 1 : null}>
                      {d.label}
                    </Chip>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-start gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-24 shrink-0 pt-1.5">Measures</span>
          <div className="flex items-center gap-1.5 flex-wrap flex-1">
            {fields.measures.map((m) => (
              <Chip key={m.id} active={measures.includes(m.id)} onClick={() => toggle(measures, setMeasures, m.id, 6)} testId={`v2-measure-${m.id}`}>{m.label}</Chip>
            ))}
          </div>
        </div>

        <div className="flex items-start gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-24 shrink-0 pt-1.5">Derived</span>
          <div className="flex items-center gap-1.5 flex-wrap flex-1">
            {fields.derived.map((m) => (
              <Chip key={m.id} active={derived.includes(m.id)} onClick={() => toggle(derived, setDerived, m.id, 4)} testId={`v2-derived-${m.id}`}>{m.label}</Chip>
            ))}
            <span className="text-[10px] text-muted-foreground">· computed on the first selected measure</span>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-24 shrink-0">Visualize</span>
          {fields.visualizations.map((v) => {
            const Icon = VIZ_ICONS[v.id] || Table2;
            return (
              <Chip key={v.id} active={viz === v.id} onClick={() => setViz(v.id)} testId={`v2-viz-${v.id}`}>
                <Icon className="w-3 h-3" /> {v.label}
              </Chip>
            );
          })}
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="rounded-sm h-7 text-xs w-[130px] ml-2" data-testid="v2-months-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["1", "3", "6", "12"].map((m) => <SelectItem key={m} value={m}>Last {m} month{m !== "1" ? "s" : ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground w-24 shrink-0">Filters</span>
          {fields.filterable.map((f) => (
            <MultiFilter key={f.id} f={f} values={filters[f.id]} testPrefix="v2-filter"
              onChange={(vals) => setFilters((prev) => ({ ...prev, [f.id]: vals }))} />
          ))}
          {Object.values(filters).some((v) => v?.length) && (
            <button data-testid="v2-filters-reset" onClick={() => setFilters({})} className="text-[11px] text-[#be185d] hover:underline">clear filters</button>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <Input data-testid="v2-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Report name (e.g. AWS Commitment Savings by BU)" className="max-w-sm h-8 text-sm" />
          <Button size="sm" data-testid="v2-run" onClick={run} disabled={loading} className="bg-[#00579e] hover:bg-[#00477f]">
            <Play className="w-3.5 h-3.5 mr-1.5" /> {loading ? "Running…" : "Run"}
          </Button>
          <Button size="sm" variant="outline" data-testid="v2-save" onClick={save}>
            <Save className="w-3.5 h-3.5 mr-1.5" /> Save
          </Button>
          <Button size="sm" variant="outline" data-testid="v2-export" onClick={exportCsv}>
            <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Preview */}
      {data && (
        <div className="space-y-4" data-testid="v2-preview">
          <div className="flex items-center gap-3 flex-wrap">
            {data.totals.map((t) => (
              <div key={t.label} className="cs-card px-4 py-2.5" data-testid={`v2-total-${t.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{t.label}</div>
                <div className="cs-mono text-lg font-semibold text-foreground">
                  {t.fmt === "usd" ? fmtUSD(t.value) : Number(t.value).toLocaleString()}
                </div>
              </div>
            ))}
            <span className="text-[11px] text-muted-foreground ml-auto cs-mono">{data.row_count} rows</span>
          </div>
          {data.row_count === 0 && (
            <div className="cs-card p-8 text-center space-y-2" data-testid="v2-no-rows">
              <div className="text-sm font-semibold text-foreground">No rows match these filters</div>
              <div className="text-xs text-muted-foreground">
                The current filter combination excludes every record. Try removing a filter or two —
                {" "}
                <button data-testid="v2-no-rows-clear" onClick={() => { setFilters({}); setData(null); }} className="text-[#be185d] hover:underline font-medium">clear all filters</button>
                {" "}and re-run.
              </div>
            </div>
          )}
          {data.row_count > 0 && data.widget && (
            <div className="cs-card p-5" data-testid="v2-chart">
              <EconWidget def={{}} data={data.widget} size="lg" />
            </div>
          )}
          {data.row_count > 0 && <V2Table table={data.tables[0]} />}
        </div>
      )}
      {!data && !loading && (
        <div className="cs-card p-10 text-center text-sm text-muted-foreground" data-testid="v2-empty">
          Pick a cloud, dimensions, measures and a visualization — then hit <span className="font-semibold text-foreground">Run</span>.
        </div>
      )}
    </div>
  );
}
